import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSupabase } from '../../lib/SupabaseProvider';
import BrandedLineLoader from '../BrandedLineLoader';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { session, loading, sessionError, retrySession } = useSupabase();
  const location = useLocation();
  const navigate = useNavigate();
  const errorHeadingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (loading || sessionError) {
      return;
    }

    if (!session) {
      // Preserve the full path (including query) into ?next= so invite
      // deep-links survive the round-trip. location.state.from remains set
      // as a fallback for any legacy callers that consume it.
      const next = `${location.pathname}${location.search}`;
      void navigate(`/login?next=${encodeURIComponent(next)}`, {
        replace: true,
        state: { from: location.pathname },
      });
      return;
    }

  }, [loading, session, sessionError, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (sessionError) errorHeadingRef.current?.focus();
  }, [sessionError]);

  if (sessionError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div
          role="alert"
          aria-labelledby="session-error-heading"
          className="w-full max-w-sm rounded-xl bg-surface p-6 text-center"
        >
          <h1
            id="session-error-heading"
            ref={errorHeadingRef}
            tabIndex={-1}
            className="text-xl font-bold text-foreground"
          >
            We couldn't check your sign-in
          </h1>
          <p className="mt-3 text-muted-foreground">
            Try checking your sign-in again. This page will stay open while we check.
          </p>
          <button
            type="button"
            onClick={() => void retrySession()}
            className="mt-6 rounded-lg button-accent px-6 py-3 font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-volt focus:ring-offset-2 focus:ring-offset-background"
          >
            Check again
          </button>
        </div>
      </div>
    );
  }

  // Single root-level branded loader. Stays on `bg-background` to match the
  // index.html boot loader and AdminLayout's loader so the admin refresh
  // is one continuous bar, not three separate loading states.
  //
  // The same loader is reused as a logout bridge below: when the session
  // flips to null (sign-out), the redirect happens via `useEffect` on the
  // next tick, so we paint ONE frame between unmounting `children` and
  // navigate(). Returning `null` for that frame caused a jarring blank
  // flash; the BrandedLineLoader on `bg-background` matches the LoginPage
  // background so the handoff stays one continuous dark surface.
  if (loading || !session) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <BrandedLineLoader logoUrl={null} />
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
